import React, { useState, useEffect } from "react";
import axios from "axios";
import Menu from "../Menu";
import { useNavigate } from "react-router-dom";
import useApiUrls from "../../useApiUrls";

const ActionOperation = () => {
  const navigate = useNavigate();
  const { getUrlByOperation } = useApiUrls();
  
  // State variables
  const [actionList, setActionList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [error, setError] = useState(null);
  
  // Modal states
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  
  // Form states
  const [formId, setFormId] = useState("");
  const [formName, setFormName] = useState("");
  const [formJson, setFormJson] = useState("");
  const [jsonValidationError, setJsonValidationError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Toast notifications
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  // Get auth credentials
  const token = getCookie("token") || getCookie("authToken") || "";
  const userid = getCookie("userid") || getCookie("userId") || "1";

  const apiHeaders = {
    "accept": "text/plain",
    "UserId": userid,
    "X-Authorization": token,
    "Content-Type": "application/json-patch+json"
  };

  // Toast helper
  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "success" });
    }, 3000);
  };

  // Fetch action list
  const fetchActions = async () => {
    const getDistinctUrl = getUrlByOperation("ActionOperationGetDistinctName");
    if (!getDistinctUrl) return;

    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        getDistinctUrl,
        { headers: apiHeaders }
      );
      
      if (response.data && response.data.isresponse && Array.isArray(response.data.data)) {
        setActionList(response.data.data);
      } else {
        setActionList([]);
        if (response.data && response.data.errormsg && response.data.errormsg !== "success") {
          setError(response.data.errormsg);
        }
      }
    } catch (err) {
      console.error("Error fetching action list:", err);
      setError("Failed to load Action Operations. " + (err.response?.data?.errormsg || err.message));
      if (err.message === "relogin token expire") {
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  // Run on mount
  useEffect(() => {
    fetchActions();
  }, [getUrlByOperation]);

  // Validate JSON string
  useEffect(() => {
    if (!formJson.trim()) {
      setJsonValidationError(null);
      return;
    }
    try {
      JSON.parse(formJson);
      setJsonValidationError(null);
    } catch (e) {
      setJsonValidationError(e.message);
    }
  }, [formJson]);



  // Add click
  const handleAddClick = () => {
    setIsEditMode(false);
    setFormId("");
    setFormName("");
    setFormJson("");
    setShowAddEditModal(true);
  };

  // Edit click (fetch data from model / Get API)
  const handleEditClick = async (actionItem) => {
    const getUrl = getUrlByOperation("ActionOperationGet");
    if (!getUrl) {
      showToast("API configuration loading, please try again", "error");
      return;
    }

    setActionLoadingId(actionItem.id);
    try {
      const response = await axios.post(
        getUrl,
        {
          id: actionItem.id,
          name: actionItem.value
        },
        { headers: apiHeaders }
      );

      if (response.data && response.data.isresponse && Array.isArray(response.data.data) && response.data.data.length > 0) {
        const detail = response.data.data[0];
        setFormId(detail.id);
        setFormName(detail.name);
        setFormJson(detail.json || "");
        setIsEditMode(true);
        setShowAddEditModal(true);
      } else {
        showToast("Failed to fetch detailed data for action", "error");
      }
    } catch (err) {
      console.error("Error getting action details:", err);
      showToast(err.response?.data?.errormsg || "Error retrieving operation details", "error");
    } finally {
      setActionLoadingId(null);
    }
  };

  // Delete click
  const handleDeleteClick = (actionItem) => {
    setSelectedAction(actionItem);
    setShowDeleteModal(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!selectedAction) return;

    const deleteUrl = getUrlByOperation("ActionOperationDelete");
    if (!deleteUrl) {
      showToast("API configuration loading, please try again", "error");
      return;
    }

    setIsSaving(true);
    try {
      const response = await axios.post(
        deleteUrl,
        { id: selectedAction.id },
        { headers: apiHeaders }
      );

      if (response.data && response.data.isresponse && response.data.responsestatus === "SUCCESS") {
        showToast("Action Operation deleted successfully", "success");
        setShowDeleteModal(false);
        fetchActions();
      } else {
        showToast(response.data?.errormsg || "Failed to delete operation", "error");
      }
    } catch (err) {
      console.error("Error deleting action:", err);
      showToast(err.response?.data?.errormsg || "Error deleting operation", "error");
    } finally {
      setIsSaving(false);
    }
  };

  // Handle Save (Insert / Update)
  const handleSave = async (e) => {
    e.preventDefault();
    if (!formName.trim()) {
      showToast("Please enter a name", "error");
      return;
    }
    if (jsonValidationError) {
      showToast("Please fix JSON validation errors before saving", "error");
      return;
    }

    const insertUrl = getUrlByOperation("ActionOperationInsert");
    const updateUrl = getUrlByOperation("ActionOperationUpdate");

    if ((isEditMode && !updateUrl) || (!isEditMode && !insertUrl)) {
      showToast("API configuration loading, please try again", "error");
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        name: formName,
        json: formJson || "{}"
      };

      let response;
      if (isEditMode) {
        payload.id = parseInt(formId);
        response = await axios.post(
          updateUrl,
          payload,
          { headers: apiHeaders }
        );
      } else {
        response = await axios.post(
          insertUrl,
          payload,
          { headers: apiHeaders }
        );
      }

      if (response.data && response.data.isresponse && response.data.responsestatus === "SUCCESS") {
        showToast(`Action Operation ${isEditMode ? 'updated' : 'created'} successfully!`, "success");
        setShowAddEditModal(false);
        fetchActions();
      } else {
        showToast(response.data?.errormsg || "An error occurred", "error");
      }
    } catch (err) {
      console.error("Error saving action operation:", err);
      showToast(err.response?.data?.errormsg || "Network or Server Error", "error");
    } finally {
      setIsSaving(false);
    }
  };

  // Filter actions
  const filteredActions = actionList.filter(item => {
    const term = searchTerm.toLowerCase();
    const idMatch = String(item.id).toLowerCase().includes(term);
    const nameMatch = String(item.value).toLowerCase().includes(term);
    return idMatch || nameMatch;
  });

  return (
    <>
      <Menu />
      
      <div className="wrapper">
        <div className="content-wrapper">
          
          {/* Content Header (Page header) */}
          <section className="content-header">
            <div className="container-fluid">
              <div className="d-flex justify-content-between align-items-center w-100">
                <h1 className="mb-0">Action Operation</h1>
                <button type="button" className="btn btn-success" onClick={handleAddClick}>
                  Add Action Operation
                </button>
              </div>
            </div>
          </section>

          {/* Main content */}
          <section className="content">
            <div className="container-fluid">
              <hr />
              <div className="row">
                <div className="col-12">
                  
                  <div className="card">
                    <div className="card-header">
                      <h3 className="card-title">Action Operation List</h3>
                      
                      <div className="card-tools">
                        <div className="input-group input-group-sm" style={{ width: "250px" }}>
                          <input
                            type="text"
                            name="table_search"
                            className="form-control float-right"
                            placeholder="Search by ID or Name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Card Body */}
                    <div className="card-body table-responsive p-0">
                      <table className="table table-hover table-striped">
                        <thead style={{
                          backgroundColor: "var(--surface2)",
                          color: "var(--text)",
                          borderColor: "var(--border)"
                        }}>
                          <tr>
                            <th style={{ width: "15%" }}>Id</th>
                            <th style={{ width: "60%" }}>Name</th>
                            <th style={{ width: "25%" }} className="text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loading ? (
                            <tr>
                              <td colSpan="3" className="text-center py-4">
                                <div className="spinner-border text-primary" role="status">
                                  <span className="sr-only">Loading...</span>
                                </div>
                              </td>
                            </tr>
                          ) : error ? (
                            <tr>
                              <td colSpan="3" className="text-center py-4">
                                <div className="alert alert-danger mb-0" role="alert">
                                  {error}
                                </div>
                              </td>
                            </tr>
                          ) : filteredActions.length === 0 ? (
                            <tr>
                              <td colSpan="3" className="text-center py-4">
                                No records found.
                              </td>
                            </tr>
                          ) : (
                            filteredActions.map((item) => (
                              <tr key={item.id}>
                                <td>{item.id}</td>
                                <td>
                                  <strong>{item.value}</strong>
                                </td>
                                <td className="text-center">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-info mr-2"
                                    onClick={() => handleEditClick(item)}
                                    disabled={actionLoadingId === item.id}
                                  >
                                    {actionLoadingId === item.id ? "Loading..." : "Edit"}
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-danger"
                                    onClick={() => handleDeleteClick(item)}
                                  >
                                    Delete
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Add / Edit Modal */}
      {showAddEditModal && (
        <div className="modal" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {isEditMode ? "Edit Action Operation" : "Add Action Operation"}
                </h5>
                <button type="button" className="close" onClick={() => setShowAddEditModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <form onSubmit={handleSave}>
                <div className="modal-body">
                  
                  {isEditMode && (
                    <div className="form-group">
                      <label>ID</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formId}
                        readOnly
                        style={{ backgroundColor: "#e9ecef" }}
                      />
                    </div>
                  )}

                  <div className="form-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter operation name"
                      value={formName}
                      onChange={(e) => setFormName(e.target.value)}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>JSON Configuration</label>
                    
                    <textarea
                      className="form-control"
                      placeholder="Enter JSON settings..."
                      value={formJson}
                      onChange={(e) => setFormJson(e.target.value)}
                      rows={10}
                      style={{ fontFamily: "monospace" }}
                    />
                    
                    {jsonValidationError ? (
                      <small className="text-danger mt-1 d-block">
                        Invalid JSON Structure: {jsonValidationError}
                      </small>
                    ) : formJson.trim() ? (
                      <small className="text-success mt-1 d-block">
                        Valid JSON Structure
                      </small>
                    ) : null}
                  </div>

                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowAddEditModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isSaving}>
                    {isSaving ? "Saving..." : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">Confirm Delete</h5>
                <button type="button" className="close text-white" onClick={() => setShowDeleteModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body text-center">
                <p>Are you sure you want to delete the operation <strong>{selectedAction?.value}</strong>?</p>
                <p className="text-muted"><small>This action cannot be undone.</small></p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={confirmDelete} disabled={isSaving}>
                  {isSaving ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast.show && (
        <div style={{ position: "fixed", top: "20px", right: "20px", zIndex: 9999, minWidth: "250px" }}>
          <div className={`alert ${toast.type === "success" ? "alert-success" : "alert-danger"} alert-dismissible fade show`} role="alert">
            {toast.message}
            <button type="button" className="close" onClick={() => setToast({ ...toast, show: false })}>
              <span>&times;</span>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ActionOperation;

// Cookie helper
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
  return null;
}
